import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Trophy, Calendar, UtensilsCrossed, ArrowRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getStats, getSettings } from '../../lib/api';
import { useAuth } from '../../lib/auth';

export const AdminDashboardPage = () => {
  const [stats, setStats] = useState({ teams: 0, players: 0, matches: 0, matches_played: 0 });
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, settingsRes] = await Promise.all([
          getStats(),
          getSettings()
        ]);
        setStats(statsRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const quickActions = [
    { 
      label: 'Gestisci Squadre', 
      path: '/admin/squadre', 
      icon: Users,
      color: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      adminOnly: true,
    },
    { 
      label: settings?.draw_completed ? 'Vedi Sorteggio' : 'Esegui Sorteggio', 
      path: '/admin/sorteggio', 
      icon: Trophy,
      color: 'bg-gold/10 text-gold border-gold/20',
      adminOnly: true,
    },
    { 
      label: 'Gestisci Partite', 
      path: '/admin/partite', 
      icon: Calendar,
      color: 'bg-green-500/10 text-green-400 border-green-500/20'
    },
    { 
      label: 'Gestisci Menu', 
      path: '/admin/menu', 
      icon: UtensilsCrossed,
      color: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
      adminOnly: true,
    },
  ].filter(a => isAdmin || !a.adminOnly);

  return (
    <div className="p-6" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">DASHBOARD</h1>
          <p className="text-slate-400">
            Gestione Torneo Notturno Viadana - 30ª Edizione
          </p>
        </div>

        {/* Status Banner */}
        <div className={`mb-8 p-4 rounded-lg border ${
          settings?.draw_completed 
            ? 'bg-green-500/10 border-green-500/20' 
            : 'bg-gold/10 border-gold/20'
        }`}>
          <p className="font-semibold">
            Stato: <span className={settings?.draw_completed ? 'text-green-400' : 'text-gold'}>
              {settings?.current_phase === 'registration' && 'Fase Iscrizioni'}
              {settings?.current_phase === 'group' && 'Fase a Gironi'}
              {settings?.current_phase === 'knockout' && 'Fase Eliminatoria'}
              {settings?.current_phase === 'finished' && 'Torneo Concluso'}
            </span>
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6 text-center">
              <Users className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="font-numbers text-4xl font-bold">{stats.teams}</p>
              <p className="text-sm text-slate-400">Squadre</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6 text-center">
              <Trophy className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="font-numbers text-4xl font-bold">{stats.players}</p>
              <p className="text-sm text-slate-400">Giocatori</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6 text-center">
              <Calendar className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="font-numbers text-4xl font-bold">{stats.matches}</p>
              <p className="text-sm text-slate-400">Partite</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6 text-center">
              <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center text-2xl">⚽</div>
              <p className="font-numbers text-4xl font-bold">{stats.matches_played}</p>
              <p className="text-sm text-slate-400">Giocate</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <h2 className="font-heading text-xl font-bold mb-4">AZIONI RAPIDE</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quickActions.map((action) => (
            <Link key={action.path} to={action.path}>
              <Card className={`bg-slate-900 border-slate-800 hover:border-slate-700 transition-colors cursor-pointer`}>
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${action.color} border`}>
                      <action.icon className="w-6 h-6" />
                    </div>
                    <span className="font-semibold">{action.label}</span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Tournament Info */}
        <Card className="mt-8 bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="font-heading">INFORMAZIONI TORNEO</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">Formato:</span>
              <span>12 squadre, 3 gironi</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Qualificazione:</span>
              <span>Prime 2 + 2 migliori 3ª</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Fase finale:</span>
              <span>Quarti e Semi (A/R) + Finali</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Giorni partita:</span>
              <span>Martedì, Giovedì, Sabato</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Data inizio:</span>
              <span className="text-primary">27 Maggio 2026</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
